import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { ROUTES } from '../constants/routes';
import { useAuth } from '../context/AuthContext';

// Sidebar + topbar layout used by all portals (Medical, Lab, QA, Admin, etc.)
const PortalLayout = ({
  sidebar,
  topbarTitle,
  topbarRight,
  brand = 'TrustLab',
  brandSub = 'Portal',
  // Optional fully-custom brand block. When set, replaces the default
  // two-Typography header at the top of the sidebar — lets a portal
  // show its own animation / logo / markup without affecting other
  // portals that still use the default.
  brandSlot,
  // Optional fully-custom footer block. When set, replaces the default
  // Sign Out button at the bottom of the sidebar. Useful when a portal
  // wants a different primary action (e.g. "Back to website" instead
  // of logging out).
  footerSlot,
  // Optional fully-custom topbar title block. When set, replaces the
  // plain-text Typography that renders `topbarTitle` — lets a portal
  // show its own richer layout (icon tile + title + eyebrow, etc.)
  // without affecting other portals' topbars.
  topbarTitleSlot,
  // Optional sidebar background — accepts any valid CSS background
  // (solid colour, gradient string, etc.). Defaults to AIMS's rail: a
  // dark teal lit from the bottom-left, so the panel has depth instead of
  // reading as one flat block.
  sidebarBg = `
    radial-gradient(120% 50% at 0% 100%, rgba(43,158,138,.26) 0%, rgba(43,158,138,0) 62%),
    linear-gradient(168deg, #08322B 0%, #0B4239 34%, #0E4F45 70%, #136055 100%)
  `,
  children,
  accent = '#BFEDE7',
  sidebarWidth = 240,
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignOut = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  const sidebarContent = (
    <>
      {/* AIMS's `.rail-brand` is one compact row at the head of the rail,
          not a deep masthead — the nav should start near the top. */}
      <Box sx={{ p: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        {brandSlot || (
          <>
            <Typography
              className="display"
              sx={{ fontSize: 23, color: accent, letterSpacing: '.045em', lineHeight: 1, fontWeight: 800 }}
            >
              {brand.toUpperCase()}
            </Typography>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '.155em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.35)',
                mt: 0.5,
              }}
            >
              {brandSub}
            </Typography>
          </>
        )}
      </Box>

      <Box
        sx={{ flex: 1, px: 1.5, py: 2, overflowY: 'auto', overscrollBehavior: 'contain' }}
        onClick={() => setMobileOpen(false)}
      >
        {sidebar}
      </Box>

      <Box sx={{ p: 1.75, borderTop: '1px solid rgba(255,255,255,0.10)', bgcolor: 'rgba(0,0,0,0.18)' }}>
        {footerSlot || (
          <Button
            fullWidth
            onClick={handleSignOut}
            startIcon={<LogoutIcon />}
            sx={{
              color: 'rgba(255,255,255,0.5)',
              bgcolor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' },
            }}
          >
            Sign Out
          </Button>
        )}
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F4F6F8' }}>
      {/* Desktop sidebar */}
      <Box
        sx={{
          width: sidebarWidth,
          background: sidebarBg,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          flexShrink: 0,
          height: '100vh',
          position: 'sticky',
          top: 0,
        }}
      >
        {sidebarContent}
      </Box>

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 260,
            background: sidebarBg,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Main column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Topbar — plain flex row: title takes all free space, right group pinned to the end */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#fff',
            borderBottom: '1px solid #DDE3E9',
            px: { xs: 2, md: 2.5 },
            minHeight: { xs: 56, md: 60 },
            position: 'sticky',
            top: 0,
            zIndex: 50,
            gap: 2,
          }}
        >
          {/* LEFT: menu (mobile) + title. `flex: 1` claims all remaining space. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, color: '#10151C' }}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
            {topbarTitleSlot || (
              <Typography
                className="display"
                sx={{
                  fontSize: { xs: 15, md: 17 },
                  letterSpacing: '.01em',
                  color: '#10151C',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {topbarTitle}
              </Typography>
            )}
          </Box>

          {/* RIGHT: actions — pinned to the far right, no shrink */}
          <Stack
            direction="row"
            spacing={{ xs: 1, md: 2 }}
            alignItems="center"
            sx={{ flexShrink: 0, ml: 'auto' }}
          >
            {topbarRight}
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 2.5, md: 2.5 }, flex: 1 }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default PortalLayout;
